import React, { ReactNode, useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Footer, FooterLink } from '../organisms/Footer';
import { Header } from '../organisms';
import { useSettings } from '../../stateManagement/SettingsContext';
import { appInsights } from '../../appInsights';
import i18n from '../../i18n';
import { Loader } from '../organisms/Loader';
// import { trackEvent } from '../../Analytics/ga';
import { ContactBtnSection } from '../molecules/ContactUsBtnSection';
import {
  GlobalUserData,
  // hmac,
  initializeHelpshiftChat,
  listenToConversationStatus,
  log,
} from '../../utils';
import { useAuth } from '../../stateManagement/auth/useAuth';

interface LayoutProps {
  children: ReactNode;
  onClickLandingPageButton?: () => void;
  onClickMyTickets?: () => void;
  onContactUsClick?: () => void;
  pageBgColor?: String;
  showContactUsButton?: boolean;
  settings: any;
  isPreviewMode?: boolean;
}

const PLATFORM_ID = process.env.REACT_APP_WEB_PLATFORM_ID || '';
const DOMAIN = process.env.REACT_APP_DOMAIN || '';
const LANGUAGE = process.env.REACT_APP_LANGUAGE || '';
// const SECRET_KEY = process.env.REACT_APP_SECRET_KEY || '';

export const Layout: React.FC<LayoutProps> = ({
  children,
  onClickLandingPageButton,
  onClickMyTickets,
  onContactUsClick,
  pageBgColor,
  showContactUsButton = true,
  settings,
  isPreviewMode = false,
}) => {
  const [darkMode, setDarkMode] = useState(false);
  const navigate = useNavigate();

  const { setSettings, language, supportedLanguages, setLanguage, unreadCount, setUnreadCount } =
    useSettings();
  // const [currentLanguage, setCurrentLanguage] = useState(language);
  const [settingsCopy, setSettingsCopy] = useState<typeof settings | null>(null);
  const { t }: { t: (key: string, options?: { returnObjects: boolean }) => any } = useTranslation();
  const translations = t('data.footerLinks', { returnObjects: true }) as FooterLink[];
  const helperTexts = t('data.helperTexts', { returnObjects: true });

  const handleLanguageChange = (newLanguage: string) => {
    const languageCode = supportedLanguages.find(lang => lang.label === newLanguage)?.value || 'en';

    i18n
      .changeLanguage(languageCode)
      .then(() => {
        // const selectedLang =
        //   supportedLanguages.find(lang => lang.label === newLanguage)?.label || newLanguage;
        // setCurrentLanguage(selectedLang);
        setLanguage(languageCode);

        // Track with GA
        // trackEvent('language_changed', {
        //   page: 'header',
        //   selected_language: languageCode,
        // });

        // Also track with App Insights
        appInsights.trackEvent({
          name: 'header',
          properties: {
            buttonName: 'languageDropdown',
            userName: 'anonymous',
            language: newLanguage,
          },
        });
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.log('Error changing language:', err);
      });
  };

  const handleMyTicketsClick = onClickMyTickets
    ? () => {
        onClickMyTickets();
      }
    : () => {
        navigate('/playerhub');
      };

  const myTicketsHref = onClickMyTickets
    ? () => {
        return undefined;
      }
    : () => {
        return '/playerhub';
      };

  const handleLandingPageClick = onClickLandingPageButton
    ? () => {
        onClickLandingPageButton();
      }
    : () => {
        navigate('/');
      };

  const landingPageHref = onClickLandingPageButton
    ? () => {
        return undefined;
      }
    : () => {
        return '/';
      };

  const handleContactUsClick = onContactUsClick
    ? () => {
        onContactUsClick();
      }
    : () => {
        navigate(`/contactUs`);
      };

  const contactUsHref = isPreviewMode
    ? () => {
        return undefined;
      }
    : () => {
        return '/contactUs';
      };

  useEffect(() => {
    if (settings && supportedLanguages.length > 0) {
      const selectedLang =
        supportedLanguages.find(lang => lang.value === language)?.label || 'English';
      // setCurrentLanguage(selectedLang);
      setLanguage(supportedLanguages.find(lang => lang.label === selectedLang)?.value || 'en');
      i18n
        .changeLanguage(language)
        // eslint-disable-next-line no-console
        .catch(err => console.log('Error setting initial language:', err));
    }
  }, [settings, setSettings]);

  const changeTheme = () => {
    setDarkMode(prevDarkMode => !prevDarkMode);

    if (!darkMode) {
      if (!settings) return;

      if (!settingsCopy) {
        setSettingsCopy({ ...settings });
      }

      const tempSettings = {
        ...settings,
        configs: {
          ...settings?.configs,
          needHelpTextColor: '#fff',
          mainHeaderBgColor: '#000',
          mainHeaderTextColor: '#fff',
          primaryBgColor: '#000',
          secondaryBgColor: '#777',
          primaryTextColor: '#fff',
          secondaryTextColor: '#fff',
          contactUsButtonBgColor: '#faba32',
          contactUsButtonTextColor: '#000',
          searchBarBgColor: '#777',
        },
      };
      setSettings(tempSettings);
    } else if (settingsCopy) {
      setSettings(settingsCopy);
      setSettingsCopy(null);
    }
  };

  useEffect(() => {
    if (!settings) {
      fetch('/api/settings')
        .then(response => response.json())
        .then(data => {
          setSettings(data);
        })
        // eslint-disable-next-line no-console
        .catch(error => console.error('Error fetching settings:', error));
    }
  }, [settings, setSettings]);

  if (!settings) {
    return <Loader />;
  }

  const { user, accessToken } = useAuth();

  // const userId = JSON.stringify(user?.idTokenClaims?.pragmaId);
  const userId: string = user?.idTokenClaims?.sub as string;
  const userEmail = user?.idTokenClaims?.emails?.[0] || '';
  const userName = user?.idTokenClaims?.name || '';
  // const steamId = user?.idTokenClaims?.steam_id as string;
  const globalUserData: GlobalUserData = {
    first_name: String(user?.idTokenClaims?.given_name || ''),
    last_name: String(user?.idTokenClaims?.family_name || ''),
    full_name: String(user?.idTokenClaims?.name || ''),
    display_name: String(user?.idTokenClaims?.name || ''),
    last_country: String(user?.idTokenClaims?.country || ''),
    last_city: String(user?.idTokenClaims?.city || ''),
    age: String(user?.idTokenClaims?.age || ''),
    lifetime_value: String(user?.idTokenClaims?.lifetimeValue || ''),
    user_persona: String(user?.idTokenClaims?.userPersona || ''),
    user_vip_segment: String(user?.idTokenClaims?.userVipSegment || ''),
    user_support_status: String(user?.idTokenClaims?.userSupportStatus || ''),
    last_active_date: String(user?.idTokenClaims?.lastActiveDate || ''),
    accepted_t_and_c: String(user?.idTokenClaims?.acceptedTAndC || false),
    preferred_language: String(user?.idTokenClaims?.preferredLanguage || ''),
  };
  const location = useLocation();

  const fetchConversationStatus = async () => {
    try {
      const status = await listenToConversationStatus();
      const activeIssues = status.open;
      log(settings?.configs.showLogs, 'Activeissues', activeIssues);
      if (activeIssues) {
        initializeHelpshiftChat({
          platformId: PLATFORM_ID,
          domain: DOMAIN,
          language: LANGUAGE,
          userName,
          userEmail,
          userId,
          // steam_id: steamId,
          globalUserData,
          accessToken,
          property: 'showWidget',
          // userAuthToken: hmac(userId, userEmail, SECRET_KEY),
        });
      }
    } catch (error) {
      log(settings?.configs.showLogs, 'Error fetching data:', error);
    }
  };
  useEffect(() => {
    initializeHelpshiftChat({
      platformId: PLATFORM_ID,
      domain: DOMAIN,
      language: LANGUAGE,
      userName,
      userEmail,
      userId,
      // steam_id: steamId,
      globalUserData,
      accessToken,
      property: 'hideWidget',
      // userAuthToken: hmac(userId, userEmail, SECRET_KEY),
    });
    fetchConversationStatus();
  }, []);

  useEffect(() => {
    if (accessToken) {
      // eslint-disable-next-line
      // console.log('Access token is available:', accessToken);
      log(settings?.configs.showLogs, 'Access token is available:', accessToken);
      fetchConversationStatus();
    }
  }, [accessToken]);

  useEffect(() => {
    const isExcludedRoute =
      location.pathname === '/contactUs' || location.pathname === '/playerhub';

    // initialization skipped on contact us and playerhub page
    if (!isExcludedRoute && user && userId && accessToken) {
      initializeHelpshiftChat({
        platformId: PLATFORM_ID,
        domain: DOMAIN,
        language: LANGUAGE,
        userName,
        userEmail,
        userId,
        // steam_id: steamId,
        globalUserData,
        accessToken,
        property: 'closeWidget',
        // userAuthToken: hmac(userId, userEmail, SECRET_KEY),
      });
    }

    if (!accessToken) {
      return () => {}; // return a no-op cleanup
    }

    const unreadHandler = (data: { unreadCount: number }) => {
      setUnreadCount(data.unreadCount); // keeps state updated on every event
    };

    const convoEndHandler = () => {
      setUnreadCount(0);
    };

    if ((window as any).Helpshift) {
      (window as any).Helpshift('addEventListener', 'newUnreadMessages', unreadHandler);
      (window as any).Helpshift('addEventListener', 'conversationEnd', convoEndHandler);
    }

    return () => {
      if ((window as any).Helpshift) {
        (window as any).Helpshift('removeEventListener', 'newUnreadMessages', unreadHandler);
        (window as any).Helpshift('removeEventListener', 'conversationEnd', convoEndHandler);
      }
    };
  }, [accessToken]);

  // useEffect(() => {
  //   if (user && userId && accessToken) {
  //     initializeHelpshiftChat({
  //       platformId: PLATFORM_ID,
  //       domain: DOMAIN,
  //       language: LANGUAGE,
  //       userName,
  //       userEmail,
  //       userId,
  //       steam_id: steamId,
  //       globalUserData,
  //       accessToken,
  //       property: 'hideWidget',
  //       userAuthToken: hmac(userId, userEmail, SECRET_KEY),
  //     });
  //   }
  // }, [accessToken]);

  // useEffect(() => {
  //   console.log('MANUAL USEFFECT CALL');
  //   if (!accessToken) {
  //     return () => {}; // return a no-op cleanup
  //   }

  //   const handler = (data: { unreadCount: number }) => {
  //     console.log('Unread message count = ', data.unreadCount);
  //     setUnreadCount(data.unreadCount); // keeps state updated on every event
  //     console.log('unreadCount state = ', unreadCount);
  //   };

  //   if ((window as any).Helpshift) {
  //     (window as any).Helpshift('addEventListener', 'newUnreadMessages', handler);
  //   }

  //   return () => {
  //     if ((window as any).Helpshift) {
  //       (window as any).Helpshift('removeEventListener', 'conversationEnd', handler);
  //     }
  //   };
  // }, [accessToken]);

  // useEffect(() => {
  //   console.log('MANUAL USEFFECT CALL');
  //   if (!accessToken) {
  //     return () => {}; // return a no-op cleanup
  //   }

  //   const handler = () => {
  //     console.log('Convo has ended ');
  //     setUnreadCount(0);
  //     console.log('unreadCount state = ', unreadCount);
  //   };

  //   if ((window as any).Helpshift) {
  //     (window as any).Helpshift('addEventListener', 'conversationEnd', handler);
  //   }

  //   return () => {
  //     if ((window as any).Helpshift) {
  //       (window as any).Helpshift('removeEventListener', 'conversationEnd', handler);
  //     }
  //   };
  // }, [accessToken]);

  return (
    <Box backgroundColor={settings?.configs.primaryBgColor}>
      {/* Header */}
      {settings?.configs?.showHeader && (
        <Box position="sticky" top="0" zIndex="1000" bg={settings?.configs.primaryBgColor}>
          <Header
            showMainHeader={settings?.configs.showMainHeader}
            mainHeaderBgColor={settings?.configs.mainHeaderBgColor}
            mainHeaderTextColor={settings?.configs.mainHeaderTextColor}
            showSubHeader={settings?.configs.showSubHeader}
            subHeaderBgColor={settings?.configs.primaryBgColor}
            subHeaderTextColor={settings?.configs.primaryTextColor}
            showHeaderLogo={settings?.configs.showHeaderLogo}
            logoUrl={settings?.configs.headerlogoUrl}
            gameTitle={t(`data.gameTitle`)}
            platforms={settings?.data.platforms}
            supportedLanguages={supportedLanguages}
            currentLanguage={language}
            onLanguageChange={handleLanguageChange}
            horizontalPadding={{ base: '1', md: '8vw', lg: '15vw' }}
            changeTheme={changeTheme}
            onClickMyTickets={handleMyTicketsClick}
            myTicketsHref={myTicketsHref}
            myTicketsButtonText={
              helperTexts.find((item: Record<string, string>) => 'myTicketsButtonText' in item)
                ?.myTicketsButtonText
            }
            loginButtonText={
              helperTexts.find((item: Record<string, string>) => 'loginButtonText' in item)
                ?.loginButtonText
            }
            logoWidth={settings?.configs?.headerLogoSize}
            // targetUrl="/"
            onClickLandingPageButton={handleLandingPageClick}
            landingPageHref={landingPageHref}
            secondaryAccentColor={settings.configs.secondaryAccentColor}
            headerTextColors={settings.configs.headerTextColors}
            onButtonClick={handleContactUsClick}
            color={settings?.configs.contactUsButtonTextColor}
            buttonBgColor={settings?.configs.contactUsButtonBgColor}
            contactUsButtonText={
              helperTexts.find((item: Record<string, string>) => 'contactUsButtonText' in item)
                ?.contactUsButtonText
            }
            unreadMessageCount={unreadCount}
            contactUsHref={contactUsHref}
            isPreviewMode={isPreviewMode}
          />
        </Box>
      )}

      {/* Render children */}
      {children}

      {showContactUsButton && settings?.configs.showContactUsButton && (
        <Box
          bgGradient={`linear(to-b, ${pageBgColor} 20%, ${settings?.configs.tertiaryAccentColor} 100%)`}
        >
          <ContactBtnSection
            pt="22vh"
            pb="14vh"
            onButtonClick={handleContactUsClick}
            variant="h1"
            contactUsButtonText={
              helperTexts.find((item: Record<string, string>) => 'contactUsButtonText' in item)
                ?.contactUsButtonText
            }
            needHelpText={t('configs.needHelpText')}
            color={settings?.configs.contactUsButtonTextColor}
            buttonBgColor={settings?.configs.contactUsButtonBgColor}
            needHelpTextColor={settings?.configs.needHelpTextColor}
            showJaggedBorder={settings?.configs.showJaggedBorder}
            showRocks={settings?.configs.showRocks}
            footerBgColor={settings?.configs.footerBackgroundColor}
            rocksImageUrl={settings?.configs.rocksImageUrl}
            contactUsHref={contactUsHref}
          />
        </Box>
      )}

      {/* Footer */}
      {settings?.configs?.showFooter && (
        <Footer
          showFooterLogo={settings?.configs?.showFooterLogo}
          gameTitle={t(`data.gameTitle`)}
          showFooterLinks={settings?.configs?.showFooterLinks}
          links={settings?.data?.footerLinks}
          translations={translations}
          imageSrc={settings?.configs?.footerLogoUrl}
          logoWidth={settings?.configs?.footerLogoSize}
          footerNote={t(`data.footerNote`)}
          footerBgColor={settings?.configs?.footerBackgroundColor}
          footerTextColor={settings?.configs?.footerTextColor}
          fontSize="xs"
          showOnetrustCookieButton={settings?.configs?.showOneTrustCookieButton}
          useCustomOneTrustButtonStyle={settings?.configs?.useCustomOneTrustButtonStyle}
          oneTrustCookieButtonText={settings?.data?.oneTrustCookieButtonText}
          openFooterLinkInNewTab={settings?.configs?.openFooterLinkInNewTab}
        />
      )}
    </Box>
  );
};
